import type { ConnectionNeeded } from "@zapier/bff";

/**
 * Cross-account copy hands `connectionsNeeded` to the destination folders page
 * through `sessionStorage`: the copy flow ends in a hard `location.href`
 * navigation (account switch) that unmounts the app, so in-memory state can't
 * survive. `sessionStorage` is same-origin and survives the switch.
 */
export const CONNECT_HANDOFF_KEY = "caa:connect";

export interface ConnectHandoff {
  /** Account the assets were copied into (the account we land in post-switch). */
  targetAccountId: string;
  /** Destination folder the copy landed in, or null for the account root. */
  destinationFolderId: string | null;
  /** Apps the copied assets need connected, from the copy response. */
  connectionsNeeded: ConnectionNeeded[];
}

function getSessionStorage(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    // Accessing sessionStorage can throw (SSR, sandboxed iframes, privacy mode).
    return undefined;
  }
}

/**
 * Persist the handoff before the post-copy account-switch redirect. No-ops when
 * there are no apps to connect.
 */
export function stashConnectHandoff(handoff: ConnectHandoff): void {
  if (handoff.connectionsNeeded.length === 0) return;
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(CONNECT_HANDOFF_KEY, JSON.stringify(handoff));
  } catch {
    // Quota/serialization failures shouldn't block the copy redirect.
  }
}

function isValidConnectionNeeded(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const item = value as { selectedApi?: unknown; targets?: unknown };
  if (typeof item.selectedApi !== "string") return false;
  if (!Array.isArray(item.targets)) return false;
  return item.targets.every((target) => {
    if (!target || typeof target !== "object") return false;
    const reference = target as { type?: unknown; id?: unknown };
    return (
      typeof reference.type === "string" && typeof reference.id === "string"
    );
  });
}

/** Read the handoff (without clearing). Returns undefined when absent/invalid. */
export function readConnectHandoff(): ConnectHandoff | undefined {
  const storage = getSessionStorage();
  if (!storage) return undefined;
  const raw = storage.getItem(CONNECT_HANDOFF_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as ConnectHandoff;
    if (
      !parsed ||
      typeof parsed.targetAccountId !== "string" ||
      !Array.isArray(parsed.connectionsNeeded) ||
      // Guard against tampered/corrupted storage with malformed items so a bad
      // `targets` shape can't crash the modal downstream.
      !parsed.connectionsNeeded.every((item) => isValidConnectionNeeded(item))
    ) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

/** Remove the handoff so it's consumed exactly once. */
export function clearConnectHandoff(): void {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(CONNECT_HANDOFF_KEY);
  } catch {
    // Ignore.
  }
}
